<template>
  <!-- Reusable "add/name" modal: every side-list add button prompts for a name
       through this instead of auto-creating a placeholder. Controlled by the
       parent's `open`; emits the trimmed name on confirm. -->
  <StModal :open="open" :title="title" @close="emit('close')">
    <StField :label="fieldLabel">
      <StTextInput
        ref="input"
        v-model="name"
        :placeholder="placeholder"
        @keyup.enter="confirm"
        @keyup.esc="emit('close')"
      />
    </StField>
    <template #footer>
      <StButton variant="ghost" @click="emit('close')">Cancel</StButton>
      <StButton variant="primary" :disabled="!allowEmpty && !name.trim()" @click="confirm">{{
        confirmLabel
      }}</StButton>
    </template>
  </StModal>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { StButton, StModal, StField, StTextInput } from './ui/index.js';

const props = withDefaults(
  defineProps<{
    open: boolean;
    title?: string;
    /** Label shown above the input. Defaults to "Name" for the add-by-name use. */
    fieldLabel?: string;
    placeholder?: string;
    confirmLabel?: string;
    /** Allow confirming with an empty value (e.g. an optional label). */
    allowEmpty?: boolean;
  }>(),
  {
    title: 'New',
    fieldLabel: 'Name',
    placeholder: 'Name…',
    confirmLabel: 'Add',
    allowEmpty: false,
  },
);

const emit = defineEmits<{ confirm: [name: string]; close: [] }>();

const name = ref('');
const input = ref<InstanceType<typeof StTextInput> | null>(null);

// Reset + focus whenever the modal opens.
watch(
  () => props.open,
  async (open) => {
    if (!open) return;
    name.value = '';
    await nextTick();
    input.value?.$el?.focus();
  },
);

function confirm() {
  const trimmed = name.value.trim();
  if (!props.allowEmpty && !trimmed) return;
  emit('confirm', trimmed);
}
</script>
